import React, { useMemo, useState } from 'react'
import { Select } from '../forms'
import TagEditor from '../TagEditor'
import { useTranslation } from 'react-i18next'
import { FormContainer, SaveIcon } from './styled'
import { saveToLocalStorage } from '../../sections/statistics/utils'
import PropTypes from 'prop-types'
import { CheckWhiteIcon } from 'media/icons'
import isEqual from 'lodash/isEqual'

// When using the ProfileSettings component, you must use useReducer in conjunction with filterReducer('../../statistics/pages/filterFragments') to store all the data in one object.
const ProfileSettings = (props) => {
	const { filters, onUpdateFilters, nameLocalStorage, ...extraOptions } = props
	const nameLocalStorageSelected = `${nameLocalStorage}_selected`
	const [existingProfiles, setExistingProfiles] = useState(JSON.parse(localStorage.getItem(nameLocalStorage)) || [])
	const [selectedProfileId, setSelectedProfileId] = useState(JSON.parse(localStorage.getItem(nameLocalStorageSelected)) || null)
	const [t] = useTranslation()

	const settingsHasChanged = useMemo(() => {
		const selectedProfile = existingProfiles.find((item) => item.id === selectedProfileId)
		if (selectedProfile) {
			return !isEqual(selectedProfile.savedSettings, filters || {})
		}
		return false
	}, [filters, selectedProfileId, existingProfiles.map((profile) => profile.savedSettings)])

	const handleSaveProfiles = profileArray => {
		setExistingProfiles(profileArray)
		saveToLocalStorage(nameLocalStorage, profileArray)
	}

	const handlerProfileChange = (profile) => {
		if(!profile) {
			setSelectedProfileId(null)
			saveToLocalStorage(nameLocalStorageSelected, null)
			return onUpdateFilters(null)
		}
		setSelectedProfileId(profile.id)
		saveToLocalStorage(nameLocalStorageSelected, profile.id)
		onUpdateFilters(profile.savedSettings)
	}

	const onSaveSettings = () => {
		const updateProfiles = [...existingProfiles]
		const index = updateProfiles.findIndex(item => item.id === selectedProfileId)
		updateProfiles[index].savedSettings = { ...filters }
		handleSaveProfiles(updateProfiles)
	}

	const actions = {
		handlerCreate({ value, setValue }) {
			if(value) {
				const newProfile = {
					id: Date.now(),
					isPinned: false,
					value,
					savedSettings: filters,
					...extraOptions
				}
				const updateProfiles = [...existingProfiles].map(profile => {
					profile.selected = false
					return profile
				})
				updateProfiles.push(newProfile)
				handlerProfileChange(newProfile)
				handleSaveProfiles(updateProfiles)
				setValue('')
			}
		},
		handlerUpdate(value, currentName) {
			if(currentName) {
				const updateProfiles = [...existingProfiles]
				const index = updateProfiles.findIndex(item => item.id === value.id)
				updateProfiles[index].value = currentName
				handleSaveProfiles(updateProfiles)
			}
		},
		duplicateFunc(value) {
			const newProfile = {
				id: Date.now(),
				isPinned: false,
				value: `${value.value}-copy`,
				savedSettings: value.savedSettings,
				...extraOptions
			}
			const updateProfiles = [...existingProfiles].map(profile => {
				profile.selected = false
				return profile
			})
			updateProfiles.push(newProfile)
			handlerProfileChange(newProfile)
			handleSaveProfiles(updateProfiles)
		},
		pinFunc(value, isPinned) {
			const updateProfiles = [...existingProfiles].map(profile => {
				profile.isPinned = false
				return profile
			})
			if (isPinned) {
				const index = updateProfiles.findIndex(item => item.id === value.id)
				updateProfiles[index].isPinned = true
			}
			handleSaveProfiles(updateProfiles)
		},
		handlerDelete(value) {
			if(value) {
				const updateProfiles = [...existingProfiles]
				const index = updateProfiles.findIndex(item => item.id === value.id)
				updateProfiles.splice(index, 1)
				handlerProfileChange(null)
				handleSaveProfiles(updateProfiles)
			}
		}
	}
	return (
		<FormContainer>
			<Select
				noForm
				inline
				style={{ width: '200px' }}
				isClearable
				valueField='id'
				labelField='value'
				value={selectedProfileId}
				options={existingProfiles}
				label={t('statistic_section.strings.user_settings')}
				onChange={handlerProfileChange}
				placeholder={t('statistic_section.placeholders.default')}
				usePlaceholder
			/>
			{settingsHasChanged ? (
				<SaveIcon onClick={onSaveSettings}>
					<CheckWhiteIcon />
				</SaveIcon>
			 ) : null}
			<TagEditor
				fieldsSelect={existingProfiles.sort((a, b) => (b.isPinned === a.isPinned ? 0 : b.isPinned ? 1 : -1))}
				tagTitles={{
					mainTitle: t('common.strings.edit_profile'),
					placeholder: t('common.placeholders.enter_profile_name')
				}}
				customActions={actions}
			/>
		</FormContainer>
	)
}

ProfileSettings.propTypes = {
	onUpdateFilters: PropTypes.func,
	additionalSaveTypes: PropTypes.object,
	nameLocalStorage: PropTypes.string,
	filters: PropTypes.object
}

export default ProfileSettings
